//#region imports
// react
import React, { useEffect, useState } from 'react';

// custom components
import TimelineContainer from '../timeline-components/timeline-container';

// bootstrap
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// styling
import './styles.css';

// pigeon map
import { Map, ZoomControl, GeoJsonFeature, GeoJson } from "pigeon-maps"
import { Overlay } from "pigeon-maps";

// redux
import { useDispatch, useSelector } from 'react-redux';
import { getEmployeeLocationData, selectLocationData } from '_redux/slices/timeline/selected-employee';
import { SelectCurrentCoordinates } from "_redux/slices/timeline/timeline-live-location-tab";


// custom components
import EmployeeLiveLocationTab from './employee-live-location-tab';
import { Colors, Spacing } from "_styles";


//#endregion imports

const LiveLocationScene = (props) => {

    // redux
    const dispatch = useDispatch();
    const locationData = useSelector(selectLocationData);
    const airportCoordinates = useSelector(SelectCurrentCoordinates);                           // airport coordinates

    // local states
    const [mapCenter, setMapCenter] = useState([0, 0]);                                         // default location of map
    const [zoom, setZoom] = useState(18);                                                       // default zoom
    const [markerList, setMarkerList] = useState([]);                                           // list of markers to show on map    
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);   // default selected date - todays date
    const [startRange, setStartRange] = useState('');                                           // start index-range for geolocation data    
    const [endRange, setEndRange] = useState('');                                               // end index-range for geolocation data   
    const [startHour, setStartHour] = useState(0);                                              // start hour for filtering geolocation data
    const [endHour, setEndHour] = useState(23);                                                 // end hour for filtering geolocation data
    const [reversedGeos, setReversedGeos] = useState([]);                                       // reversed geolocation data to show on map
    const [lines, setLines] = useState([]);                                                     // lines to show on map
    const [empId, setEmpId] = useState(null);                                                   // employee id from url
    const [empName, setEmpName] = useState('');                                                 // employee name from url
    const [airport, setAirport] = useState('');                                                 // airport iata from url
    //#region helpers
    /**
     * 
     * @param {*} geo 
     * @param {*} index 
     * @param {*} start 
     * @returns overlay component for the map marker.
     */
    const overlayMaker = (geo, index, start) => {
        return (
            <Overlay anchor={[geo.latitude, geo.longitude]} offset={[10, 0]} key={start + index}>
                <div style={{
                    backgroundColor: '#2d78faff',
                    borderRadius: '50%',
                    width: 10,
                    height: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                </div>
            </Overlay>
        )
    }

    const overlayLineMaker = (start, end) => ({
        type: "Feature",
        properties: {},
        geometry: {
            type: "LineString",
            coordinates: [[start[1], start[0]], [end[1], end[0]]]
        },
    });
    //#endregion helpers

    //#region use effects
    /**
     * Effect runs on render to fetch employee location data AND also when date changes
     */
    useEffect(() => {

        // get employeeId and airport from window url
        const urlParams = new URLSearchParams(window.location.search);
        const employeeId = urlParams.get("employeeId");
        const airport = urlParams.get("airport");
        let name = urlParams.get("empName");
        name = name.replace(/%20/g, ' ');

        if (employeeId && airport) {

            // get employee id and iata
            let iata = airport;
            setEmpId(employeeId);
            setEmpName(name);
            setAirport(iata);

            // fetch employee location data for the selected date
            dispatch(getEmployeeLocationData({
                employeeId: employeeId,
                iata: iata,
                date: selectedDate
            }))

        }
    }, [selectedDate])

    /**
     * Effect runs when locationData of the employee is acquired.
     * It checks if the locationData has geolocation data,
     * reverses the geolocation array, sets the map center to the first geolocation,
     * and creates markers for each geolocation to display on the map.
     */
    useEffect(async () => {
        console.log('locationData', locationData);
        if (locationData && locationData.geolocation && locationData.geolocation.length > 0) {
            let tempGeoArray = [...locationData.geolocation].reverse();
            setReversedGeos(tempGeoArray);

            // use first geolocaiton in list to center map
            let firstGeoLocation = tempGeoArray[0];
            setMapCenter([firstGeoLocation.latitude, firstGeoLocation.longitude]);

            // create marker list using geolocation data
            let markers = tempGeoArray.map((geo, index) => overlayMaker(geo, index, 1));
            let lines = tempGeoArray.map((geo, index) => {
                if (index < tempGeoArray.length - 1) {
                    const start = [geo.latitude, geo.longitude];
                    const end = [tempGeoArray[index + 1].latitude, tempGeoArray[index + 1].longitude];
                    return overlayLineMaker(start, end);
                }
            }).filter(line => line !== undefined);
            console.log('lines', lines);
            setMarkerList(markers);
            setLines(lines);
            setStartRange(1);
            setEndRange(tempGeoArray.length);
        }
        else {
            // if no geolocation data, reset marker list and map center
            setMarkerList([]);
            setMapCenter([parseFloat(airportCoordinates?.lat), parseFloat(airportCoordinates?.lon)]);
            setReversedGeos([]);
            setLines([]);
        }
    }, [locationData]);

    /**
     * Effect runs when startRange or endRange changes.
     * It slices the local state reversedGeos array based on the start and end range,
     * and updates the markerList state with the sliced markers.
     */
    useEffect(() => {
        if (!reversedGeos || reversedGeos.length === 0) return;

        // validate range
        const start = Math.max(1, parseInt(startRange));
        const end = Math.min(parseInt(endRange), reversedGeos.length);

        if (isNaN(start) || isNaN(end) || start > end) {
            setMarkerList([]); // fallback to empty if invalid range
            return;
        }

        // slice and show only the markers in range
        const slicedGeos = reversedGeos.slice(start - 1, end);
        if (slicedGeos.length > 0) {
            const filteredMarkers = slicedGeos.map((geo, index) => overlayMaker(geo, index, start));
            let lines = slicedGeos.map((geo, index) => {
                if (index < slicedGeos.length - 1) {
                    const start = [geo.latitude, geo.longitude];
                    const end = [slicedGeos[index + 1].latitude, slicedGeos[index + 1].longitude];
                    return overlayLineMaker(start, end);
                }
            }).filter(line => line !== undefined);
            setLines(lines);
            setMarkerList(filteredMarkers);
            setMapCenter([slicedGeos[0].latitude, slicedGeos[0].longitude]);
        }
        else {
            alert('No geolocation data found for the selected range.');
            setMapCenter([parseFloat(airportCoordinates?.lat), parseFloat(airportCoordinates?.lon)]);
            setMarkerList([]);
            setLines([]);
        }
    }, [startRange, endRange, reversedGeos]);

    /**
     * Effect runs when startHour or endHour changes.
     * It filters the local state reversedGeos array based on the selected hour range,
     * and updates the markerList state with the filtered markers.
     */
    useEffect(() => {
        if (!reversedGeos || reversedGeos.length === 0) return;

        const start = parseInt(startHour, 10);
        const end = parseInt(endHour, 10);

        if (isNaN(start) || isNaN(end) || start > end || start < 0 || end > 23) {
            setMarkerList([]);
            return;
        }
        const filteredGeos = reversedGeos.filter((geo) => {
            const geoDate = new Date(geo.timestamp);
            const geoHour = geoDate.getHours();
            return geoHour >= start && geoHour <= end;
        });

        const filteredMarkers = filteredGeos.map((geo, index) => overlayMaker(geo, index, 1));
        let lines = filteredGeos.map((geo, index) => {
            if (index < filteredGeos.length - 1) {
                const start = [geo.latitude, geo.longitude];
                const end = [filteredGeos[index + 1].latitude, filteredGeos[index + 1].longitude];
                return overlayLineMaker(start, end);
            }
        }).filter(line => line !== undefined);
        setLines(lines);
        setMarkerList(filteredMarkers);

        if (filteredGeos.length > 0) {
            setMapCenter([filteredGeos[0].latitude, filteredGeos[0].longitude]);
        }
        else {
            alert('No geolocation data found for the selected hour range.');
            setMapCenter([parseFloat(airportCoordinates?.lat), parseFloat(airportCoordinates?.lon)]);
            setMarkerList([]);
            setLines([]);
        }
    }, [startHour, endHour, reversedGeos]);
    //#endregion use effects

    /**
     * Main Component.
     * It displays a timeline container with a row containing two columns:  
     * One for the EmployeeLiveLocationTab and another for the map.
     */
    return (
        <TimelineContainer>

            <div className='d-flex gap-1' style={{ height: '99vh', width: '100%', padding: Spacing.SCALE_8 }}>
                {/* sidebar */}
                <EmployeeLiveLocationTab
                    dateProps={{ selectedDate, setSelectedDate }}
                    rangeProps={{ startRange, setStartRange, endRange, setEndRange }}
                    hourProps={{ startHour, setStartHour, endHour, setEndHour }}
                    empName={empName}
                    empId={empId}
                    airport={airport}
                />

                {/* map */}
                <div className='bg-light border rounded overflow-hidden' style={{ height: '100%', width: '100%' }}>
                    <div
                        id='mapContainerDiv'
                        style={{ height: '90%', width: '100%', position: 'relative' }}>

                        {/* pigeon map */}
                        <Map
                            center={mapCenter}
                            zoom={zoom}
                            onBoundsChanged={({ center, zoom }) => {
                                setMapCenter(center);
                                setZoom(zoom);
                            }}
                            defaultZoom={18}
                            maxZoom={19}
                            minZoom={5}
                        >

                            {/* markers for employee locations */}
                            {markerList}

                            {/* lines connecting the markers */}
                            <GeoJson
                                data={{
                                    type: "FeatureCollection",
                                    features: lines,
                                }}
                                svgAttributes={{
                                    id: "VesslePath1",
                                    strokeWidth: "3",
                                    stroke: "#13c3f4ec",
                                    fill: "none",
                                    pathLength: "1",
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    borderRadius: "5px",
                                    opacity: 0.8,
                                    zIndex: 9999,

                                }}

                            />

                            {/* buttons for zooming in and out */}
                            <ZoomControl />
                        </Map>
                    </div>
                </div>
            </div>

        </TimelineContainer>
    );
};

export default LiveLocationScene;
